import type {
  ClipboardEvent as ReactClipboardEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';

import type { ConversationReference } from '~/types/api.graphql';

import { buildChipElement, MENTION_CHIP_CLASS, PENDING_CHIP_CLASS } from './mentionChipUtils';
import {
  CHIP_CARET_SPACER,
  CHIP_CARET_SPACER_LENGTH,
  createDismissedMentionText,
  DISMISSED_MENTION_MARKER,
  escapeMentionTriggers,
  getDismissedMentionDeleteFrom,
  getDismissedMentionDeleteTo,
  getNextTextOffset,
  getTextLength,
  hasMentionInputContent,
  normalizeMentionQuery,
  parseMentionMarkdown,
  stripInternalInvisibleMarkers,
} from './mentionInputModel';

interface ChipCaretSpacer {
  chip: HTMLElement;
  node: Text;
  text: string;
}

type DeletionKey = 'Backspace' | 'Delete';

// TODO: remove once TypeScript ships Selection.modify in lib.dom.d.ts.
// MDN: https://developer.mozilla.org/en-US/docs/Web/API/Selection/modify
type SelectionWithModify = Selection & {
  modify(
    alter: 'move' | 'extend',
    direction: 'forward' | 'backward' | 'left' | 'right',
    granularity: 'character' | 'word' | 'sentence' | 'line' | 'lineboundary' | 'paragraphboundary' | 'documentboundary',
  ): void;
};

export const EDITOR_BLOCK_TAGS = ['DIV', 'P', 'LI', 'BLOCKQUOTE'];

export const createReferenceChip = (ref: ConversationReference) =>
  buildChipElement({
    name: ref.name,
    deepLink: ref.deepLink,
    audienceCondition: ref.audienceCondition,
    type: ref.type,
  });

export const getNodeText = (node: Node) => node.textContent ?? '';

export const serializeEditorContent = (editorEl: HTMLElement) => {
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return getNodeText(node);

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      // Pending chip is a mid-query span, not a finalized mention; serialize as plain text.
      if (el.classList.contains(PENDING_CHIP_CLASS)) return getNodeText(el);

      if (el.classList.contains(MENTION_CHIP_CLASS)) {
        const name: string = el.dataset.name ?? '';
        const deeplink: string = el.dataset.deeplink ?? '';
        return `[${name}](${deeplink})`;
      }

      if (el.tagName === 'BR') return '\n';

      const isBlock: boolean = EDITOR_BLOCK_TAGS.includes(el.tagName);
      const inner: string = Array.from(el.childNodes).map(walk).join('');
      return isBlock ? `\n${inner}` : inner;
    }

    return '';
  };

  const serializedContent: string = Array.from(editorEl.childNodes).map(walk).join('');
  return stripInternalInvisibleMarkers(serializedContent).trim();
};

export const setCaretAt = (sel: Selection, node: Node, offset: number) => {
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const setCaretBeforeNode = (sel: Selection, node: Node) => {
  const range = document.createRange();
  range.setStartBefore(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const setCaretAfterNode = (sel: Selection, node: Node) => {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const setCaretAtEndOfElement = (sel: Selection, el: HTMLElement) => {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

const getNodeTextLength = (node: Node): number => getTextLength(getNodeText(node));

const getSelectionFocusOffset = (sel: Selection) => {
  const focusOffset: unknown = sel.focusOffset;
  return typeof focusOffset === 'number' ? focusOffset : 0;
};

export const setCaretAfterTextNode = (sel: Selection, node: Text) => {
  setCaretAt(sel, node, getNodeTextLength(node));
};

export const dispatchInputEvent = (el: HTMLElement) => {
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

export const isPlainKeyEvent = (e: ReactKeyboardEvent<HTMLDivElement>) =>
  !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;

const isPlainBackspaceEvent = (e: ReactKeyboardEvent<HTMLDivElement>) => e.key === 'Backspace' && isPlainKeyEvent(e);

const isPlainDeleteKeyEvent = (e: ReactKeyboardEvent<HTMLDivElement>) => e.key === 'Delete' && isPlainKeyEvent(e);

export const isPlainDeletionKeyEvent = (
  e: ReactKeyboardEvent<HTMLDivElement>,
): e is ReactKeyboardEvent<HTMLDivElement> & { key: DeletionKey } =>
  isPlainBackspaceEvent(e) || isPlainDeleteKeyEvent(e);

export const computeHasContent = (el: HTMLElement) =>
  hasMentionInputContent({
    text: getNodeText(el),
    hasMentionChip: el.querySelector(`.${MENTION_CHIP_CLASS as string}`) !== null,
  });

export const getPendingChip = (el: HTMLElement): HTMLElement | null =>
  el.querySelector(`.${PENDING_CHIP_CLASS as string}`);

export const getPendingChipFromSelection = (sel: Selection | null) => {
  const focusNode = sel?.focusNode;
  if (!focusNode) return null;

  const focusElement = focusNode.nodeType === Node.TEXT_NODE ? focusNode.parentElement : (focusNode as HTMLElement);
  const pendingChip = focusElement?.closest?.(`.${PENDING_CHIP_CLASS as string}`);
  return pendingChip instanceof HTMLElement ? pendingChip : null;
};

export const clearEditorIfEmpty = (el: HTMLElement) => {
  const hasText = stripInternalInvisibleMarkers(getNodeText(el)).length > 0;
  const hasChip = el.querySelector(`.${MENTION_CHIP_CLASS as string}`) !== null;
  if (hasText || hasChip) return;

  el.innerHTML = '';
  const sel = window.getSelection();
  if (sel) setCaretAtEndOfElement(sel, el);
};

// Hidden spacer text gives the caret a valid position after contenteditable=false chips.
const createChipSpacer = () => document.createTextNode(CHIP_CARET_SPACER);

export const isMentionChip = (node: Node | null): node is HTMLElement =>
  node instanceof HTMLElement && node.classList.contains(MENTION_CHIP_CLASS);

const getChipCaretSpacer = (node: Node | null): ChipCaretSpacer | null => {
  if (node?.nodeType !== Node.TEXT_NODE) return null;

  const previousSibling = node.previousSibling;
  if (!isMentionChip(previousSibling)) return null;

  const text = getNodeText(node);
  if (!text.startsWith(CHIP_CARET_SPACER)) return null;

  return { chip: previousSibling, node: node as Text, text };
};

const getStandaloneChipCaretSpacer = (node: Node | null): ChipCaretSpacer | null => {
  const spacer = getChipCaretSpacer(node);
  return spacer?.text === CHIP_CARET_SPACER ? spacer : null;
};

const setCaretBeforeChip = (sel: Selection, chip: HTMLElement) => {
  const previous = chip.previousSibling;
  if (previous?.nodeType === Node.TEXT_NODE) {
    setCaretAt(sel, previous, getNodeTextLength(previous));
    return;
  }
  setCaretBeforeNode(sel, chip);
};

const setCaretAfterChip = (sel: Selection, chip: HTMLElement) => {
  const next = chip.nextSibling;
  if (next?.nodeType === Node.TEXT_NODE) {
    const offset: number = getChipCaretSpacer(next) ? CHIP_CARET_SPACER_LENGTH : 0;
    setCaretAt(sel, next, offset);
    return;
  }
  setCaretAfterNode(sel, chip);
};

const moveCaretRightFromAfterChip = (sel: Selection, spacer: ChipCaretSpacer) => {
  if (spacer.text.length > CHIP_CARET_SPACER_LENGTH) {
    setCaretAt(sel, spacer.node, getNextTextOffset(spacer.text, CHIP_CARET_SPACER.length));
    return;
  }

  const next = spacer.node.nextSibling;
  if (next?.nodeType === Node.TEXT_NODE) {
    const nextOffset: number = Math.min(1, getNodeTextLength(next));
    setCaretAt(sel, next, nextOffset);
    return;
  }

  if (isMentionChip(next)) {
    setCaretAfterChip(sel, next);
    return;
  }

  setCaretAfterTextNode(sel, spacer.node);
};

const getChipAfterTextSelection = ({ node, offset, text }: { node: Text; offset: number; text: string }) => {
  if (offset !== getTextLength(text) || !isMentionChip(node.nextSibling)) return null;
  return node.nextSibling;
};

const getChipAfterEditorOffset = (editor: HTMLElement, offset: number) => {
  const next = editor.childNodes[offset];
  return isMentionChip(next) ? next : null;
};

const getChipBeforeEditorOffset = (editor: HTMLElement, offset: number) => {
  const previous = editor.childNodes[offset - 1];
  const previousSpacer = getStandaloneChipCaretSpacer(previous);
  if (previousSpacer) return previousSpacer.chip;

  if (isMentionChip(previous)) return previous;

  return null;
};

const getChipBeforeTextSelection = ({ node, offset }: { node: Text; offset: number }) => {
  if (offset !== 0) return null;

  const previousSpacer = getStandaloneChipCaretSpacer(node.previousSibling);
  if (previousSpacer) return previousSpacer.chip;

  if (isMentionChip(node.previousSibling)) return node.previousSibling;

  return null;
};

const getChipBeforeEditorSelection = (sel: Selection | null, editor: HTMLElement | null) => {
  if (!sel?.isCollapsed || sel.focusNode !== editor || !editor) return null;

  const focusOffset: number = sel.focusOffset;
  return getChipBeforeEditorOffset(editor, focusOffset);
};

const getChipAfterEditorSelection = (sel: Selection | null, editor: HTMLElement | null) => {
  if (!sel?.isCollapsed || sel.focusNode !== editor || !editor) return null;

  const focusOffset: number = sel.focusOffset;
  return getChipAfterEditorOffset(editor, focusOffset);
};

const getChipBeforeFocus = (focusNode: Node | null, focusOffset: number, editor: HTMLElement) => {
  if (focusNode === editor) return getChipBeforeEditorOffset(editor, focusOffset);
  if (focusNode?.nodeType !== Node.TEXT_NODE) return null;

  return getChipBeforeTextSelection({ node: focusNode as Text, offset: focusOffset });
};

const getChipAfterFocus = (focusNode: Node | null, focusOffset: number, editor: HTMLElement) => {
  if (focusNode === editor) return getChipAfterEditorOffset(editor, focusOffset);
  if (focusNode?.nodeType !== Node.TEXT_NODE) return null;

  const node = focusNode as Text;
  return getChipAfterTextSelection({ node, offset: focusOffset, text: getNodeText(node) });
};

const insertChipAtRange = (range: Range, chip: HTMLSpanElement, sel: Selection) => {
  range.insertNode(chip);

  const spacer = createChipSpacer();
  chip.parentNode?.insertBefore(spacer, chip.nextSibling);
  setCaretAfterTextNode(sel, spacer);
};

const setCaretAfterLastInsertedNode = (sel: Selection, nodes: Node[]) => {
  const lastNode = nodes[nodes.length - 1];
  if (!lastNode) return;

  if (lastNode.nodeType === Node.TEXT_NODE) {
    setCaretAt(sel, lastNode, getNodeTextLength(lastNode));
    return;
  }

  setCaretAfterNode(sel, lastNode);
};

const getCollapsedTextSelection = (sel: Selection | null): { node: Text; offset: number; text: string } | null => {
  if (!sel?.isCollapsed || sel.focusNode?.nodeType !== Node.TEXT_NODE) return null;

  const node = sel.focusNode as Text;
  return { node, offset: sel.focusOffset, text: getNodeText(node) };
};

const removeChipWithSpacer = (chip: HTMLElement) => {
  const spacer = chip.nextSibling;
  const chipSpacer = getChipCaretSpacer(spacer);
  if (chipSpacer) {
    if (chipSpacer.text === CHIP_CARET_SPACER) {
      chipSpacer.node.parentNode?.removeChild(chipSpacer.node);
    } else {
      chipSpacer.node.textContent = chipSpacer.text.slice(CHIP_CARET_SPACER_LENGTH);
    }
  }
  chip.parentNode?.removeChild(chip);
};

export const replacePendingChipWithText = ({
  pendingChip,
  text,
  restoreCaret,
}: {
  pendingChip: HTMLElement;
  text: string;
  restoreCaret: boolean;
}) => {
  const textNode = document.createTextNode(text);
  pendingChip.parentNode?.replaceChild(textNode, pendingChip);

  if (restoreCaret) {
    const sel = window.getSelection();
    if (sel) setCaretAfterNode(sel, textNode);
  }
};

export const shouldRestoreCaretAfterPendingChipRemoval = (editor: HTMLElement, pendingChip: HTMLElement) => {
  const sel = window.getSelection();
  return !sel?.focusNode || sel.focusNode === editor || pendingChip.contains(sel.focusNode);
};

export const shouldRemovePendingChipFromDelete = (e: ReactKeyboardEvent<HTMLDivElement>, pendingChip: HTMLElement) => {
  const sel = window.getSelection();
  if (!sel?.isCollapsed || !pendingChip.contains(sel.focusNode)) return false;

  const text = pendingChip.textContent ?? '';
  if (text.length > 1) return false;

  return isPlainDeletionKeyEvent(e);
};

const replacePendingChipWithMention = (pendingChip: HTMLElement, chip: HTMLSpanElement, sel: Selection | null) => {
  const spacer = createChipSpacer();
  const parent = pendingChip.parentNode;
  if (!parent) return;

  parent.insertBefore(chip, pendingChip);
  parent.insertBefore(spacer, pendingChip);
  parent.removeChild(pendingChip);

  if (sel) setCaretAfterTextNode(sel, spacer);
};

export const handleDeletionAroundInternalMarkers = (
  key: DeletionKey,
  sel: Selection | null,
  editor: HTMLElement | null,
) => {
  if (key === 'Delete') return handleDeleteAroundInternalMarkers(sel, editor);

  const chipBeforeEditorCaret = getChipBeforeEditorSelection(sel, editor);
  if (chipBeforeEditorCaret) {
    removeChipWithSpacer(chipBeforeEditorCaret);
    if (editor) dispatchInputEvent(editor);
    return true;
  }

  const textSelection = getCollapsedTextSelection(sel);
  if (!textSelection || !sel) return false;

  const { node, offset, text } = textSelection;
  const chipSpacer = getChipCaretSpacer(node);
  const chipBeforeCaret = getChipBeforeTextSelection({ node, offset });
  if (chipBeforeCaret) {
    removeChipWithSpacer(chipBeforeCaret);
    if (editor) dispatchInputEvent(editor);
    return true;
  }

  if (chipSpacer && offset <= CHIP_CARET_SPACER_LENGTH) {
    removeChipWithSpacer(chipSpacer.chip);
    if (chipSpacer.text.length > CHIP_CARET_SPACER_LENGTH) setCaretAt(sel, chipSpacer.node, 0);
    if (editor) dispatchInputEvent(editor);
    return true;
  }

  const deleteFrom: number | null = getDismissedMentionDeleteFrom(text, offset);
  if (deleteFrom === null) return false;

  node.textContent = text.slice(0, deleteFrom).concat(text.slice(offset));
  setCaretAt(sel, node, deleteFrom);
  if (editor) dispatchInputEvent(editor);
  return true;
};

export const handlePlainArrowAroundChipBoundary = (
  key: 'ArrowLeft' | 'ArrowRight',
  sel: Selection | null,
  editor: HTMLElement | null,
) => {
  if (!sel?.isCollapsed) return false;

  if (sel.focusNode === editor && editor) {
    const offset: number = sel.focusOffset;
    const previous = editor.childNodes[offset - 1];
    const next = editor.childNodes[offset];

    if (key === 'ArrowLeft') {
      const previousSpacer = getStandaloneChipCaretSpacer(previous);
      if (previousSpacer) {
        setCaretBeforeChip(sel, previousSpacer.chip);
        return true;
      }

      if (isMentionChip(previous)) {
        setCaretBeforeChip(sel, previous);
        return true;
      }
    }

    if (key === 'ArrowRight') {
      if (isMentionChip(next)) {
        setCaretAfterChip(sel, next);
        return true;
      }

      const nextSpacer = getStandaloneChipCaretSpacer(next);
      if (nextSpacer) {
        moveCaretRightFromAfterChip(sel, nextSpacer);
        return true;
      }
    }
  }

  const textSelection = getCollapsedTextSelection(sel);
  if (!textSelection || !sel) return false;

  const { node, offset, text } = textSelection;
  const chipSpacer = getChipCaretSpacer(node);
  const standaloneSpacer = getStandaloneChipCaretSpacer(node);
  const chipAfterCaret = getChipAfterTextSelection({ node, offset, text });
  const chipBeforeCaret = getChipBeforeTextSelection({ node, offset });
  if (chipAfterCaret && key === 'ArrowRight') {
    setCaretAfterChip(sel, chipAfterCaret);
    return true;
  }

  if (chipBeforeCaret && key === 'ArrowLeft') {
    setCaretBeforeChip(sel, chipBeforeCaret);
    return true;
  }

  if (standaloneSpacer) {
    if (key === 'ArrowLeft') {
      setCaretBeforeChip(sel, standaloneSpacer.chip);
    } else {
      moveCaretRightFromAfterChip(sel, standaloneSpacer);
    }
    return true;
  }

  if (chipSpacer && key === 'ArrowRight' && offset === 0) {
    setCaretAt(sel, node, CHIP_CARET_SPACER.length);
    return true;
  }

  if (chipSpacer && key === 'ArrowRight' && offset === CHIP_CARET_SPACER_LENGTH) {
    moveCaretRightFromAfterChip(sel, chipSpacer);
    return true;
  }

  if (chipSpacer && key === 'ArrowLeft' && offset === CHIP_CARET_SPACER_LENGTH) {
    setCaretBeforeChip(sel, chipSpacer.chip);
    return true;
  }

  return false;
};

export const scanMentionQuery = () => {
  const sel = window.getSelection();
  if (!sel?.focusNode) return null;

  const focusNode = sel.focusNode;
  const focusOffset: number = sel.focusOffset;
  const nodeEl = focusNode.nodeType === Node.TEXT_NODE ? focusNode.parentElement : (focusNode as HTMLElement);
  const pendingSpan = nodeEl?.closest?.(`.${PENDING_CHIP_CLASS as string}`);
  if (pendingSpan) {
    const spanText = pendingSpan.textContent ?? '';
    if (!spanText.startsWith('@') || spanText.includes('\n')) return null;
    return { query: normalizeMentionQuery(spanText.slice(1)) };
  }

  if (focusNode.nodeType !== Node.TEXT_NODE) return null;

  const text: string = getNodeText(focusNode);
  const before: string = text.slice(0, focusOffset);
  const atIndex: number = before.lastIndexOf('@');
  if (atIndex === -1 || (atIndex > 0 && !/\s/.test(before[atIndex - 1]))) return null;

  const fragment: string = before.slice(atIndex + 1);
  if (fragment.includes('\n')) return null;
  if (fragment.startsWith(DISMISSED_MENTION_MARKER)) return null;

  return { query: normalizeMentionQuery(fragment) };
};

export const wrapPendingChip = () => {
  const sel = window.getSelection();
  if (!sel?.focusNode) return;

  const focusNode = sel.focusNode;
  const focusOffset: number = sel.focusOffset;

  if (focusNode.parentElement?.classList.contains(PENDING_CHIP_CLASS)) return;
  if (focusNode.nodeType !== Node.TEXT_NODE) return;

  const text: string = getNodeText(focusNode);
  const before: string = text.slice(0, focusOffset);
  const atIndex: number = before.lastIndexOf('@');
  if (atIndex === -1) return;

  const span = document.createElement('span');
  span.className = PENDING_CHIP_CLASS;

  const queryText: string = text.slice(atIndex, focusOffset);
  const afterText: string = text.slice(focusOffset);

  (focusNode as Text).textContent = text.slice(0, atIndex);
  const spanText = document.createTextNode(queryText);
  span.appendChild(spanText);

  const parent = focusNode.parentNode;
  const nextSibling = focusNode.nextSibling;
  if (!parent) return;

  parent.insertBefore(span, nextSibling);

  if (afterText) {
    parent.insertBefore(document.createTextNode(afterText), nextSibling);
  }

  setCaretAfterTextNode(sel, spanText);
};

export const insertInlineMentionReference = (editor: HTMLElement, ref: ConversationReference) => {
  const pendingSpan = getPendingChip(editor);
  if (pendingSpan) {
    replacePendingChipWithMention(pendingSpan, createReferenceChip(ref), window.getSelection());
    return true;
  }

  const sel = window.getSelection();
  if (!sel?.focusNode || sel.focusNode.nodeType !== Node.TEXT_NODE || !editor.contains(sel.focusNode)) return false;

  const focusNode = sel.focusNode as Text;
  const focusOffset: number = sel.focusOffset;
  const text: string = getNodeText(focusNode);
  const before: string = text.slice(0, focusOffset);
  const atIndex: number = before.lastIndexOf('@');
  if (atIndex === -1) return false;

  const after: string = text.slice(focusOffset);
  focusNode.textContent = text.slice(0, atIndex).concat(after);

  const range = document.createRange();
  range.setStart(focusNode, atIndex);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);

  insertChipAtRange(range, createReferenceChip(ref), sel);
  return true;
};

export const insertReferenceAtCaret = (editor: HTMLElement, ref: ConversationReference, savedRange?: Range | null) => {
  editor.focus();

  const sel = window.getSelection();
  if (!sel) return false;

  // Prefer the saved range (captured before the popover stole focus), since
  // editor.focus() may reset the caret to the start of the contenteditable.
  if (savedRange && editor.contains(savedRange.startContainer)) {
    sel.removeAllRanges();
    sel.addRange(savedRange);
  } else if (sel.rangeCount === 0 || !sel.focusNode || !editor.contains(sel.focusNode)) {
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  const range = sel.getRangeAt(0);
  range.deleteContents();
  insertChipAtRange(range, createReferenceChip(ref), sel);
  return true;
};

export const dismissPendingMention = (editor: HTMLElement) => {
  const pendingSpan = getPendingChip(editor);
  if (!pendingSpan) return;

  replacePendingChipWithText({
    pendingChip: pendingSpan,
    text: createDismissedMentionText(getNodeText(pendingSpan)),
    restoreCaret: true,
  });
};

export const writeSelectionToClipboard = (e: ReactClipboardEvent<HTMLDivElement>, editor: HTMLElement) => {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || !sel.focusNode || !editor.contains(sel.focusNode)) return null;

  const range = sel.getRangeAt(0);
  const fragment = range.cloneContents();
  const tmp = document.createElement('div');
  tmp.appendChild(fragment);
  // Clone selection into a temporary editor fragment to reuse mention serialization.
  const text = serializeEditorContent(tmp);

  e.preventDefault();
  e.clipboardData.setData('text/plain', text);
  return range;
};

export const insertClipboardText = (editor: HTMLElement, text: string) => {
  if (!text) return false;

  const sel = window.getSelection();
  if (!sel) return false;

  if (!sel.isCollapsed && sel.rangeCount > 0) {
    sel.getRangeAt(0).deleteContents();
  }

  let range: Range;
  if (sel.rangeCount > 0 && sel.focusNode && editor.contains(sel.focusNode)) {
    range = sel.getRangeAt(0);
  } else {
    range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
  }

  const nodes: Node[] = [];
  parseMentionMarkdown(text).forEach((segment) => {
    if (segment.type === 'text') {
      nodes.push(document.createTextNode(escapeMentionTriggers(segment.text)));
      return;
    }

    nodes.push(buildChipElement({ name: segment.name, deepLink: segment.deepLink }), createChipSpacer());
  });

  const frag = document.createDocumentFragment();
  nodes.forEach((node) => frag.appendChild(node));
  range.insertNode(frag);
  setCaretAfterLastInsertedNode(sel, nodes);

  return true;
};

const handleDeleteAroundInternalMarkers = (sel: Selection | null, editor: HTMLElement | null) => {
  const chipAfterEditorCaret = getChipAfterEditorSelection(sel, editor);
  if (chipAfterEditorCaret) {
    removeChipWithSpacer(chipAfterEditorCaret);
    if (editor) dispatchInputEvent(editor);
    return true;
  }

  const textSelection = getCollapsedTextSelection(sel);
  if (!textSelection || !sel) return false;

  const { node, offset, text } = textSelection;
  const chipAfterCaret = getChipAfterTextSelection({ node, offset, text });
  if (chipAfterCaret) {
    removeChipWithSpacer(chipAfterCaret);
    if (editor) dispatchInputEvent(editor);
    return true;
  }

  const deleteTo: number | null = getDismissedMentionDeleteTo(text, offset);
  if (deleteTo === null) return false;

  node.textContent = text.slice(0, offset).concat(text.slice(deleteTo));
  setCaretAt(sel, node, offset);
  if (editor) dispatchInputEvent(editor);

  return true;
};

const getSelectionWithModify = () => {
  const sel = window.getSelection();
  return sel && 'modify' in sel ? (sel as SelectionWithModify) : null;
};

const getLineBoundaryDirection = (key: string): 'left' | 'right' | null => {
  if (key === 'ArrowLeft' || key === 'Home') return 'left';
  if (key === 'ArrowRight' || key === 'End') return 'right';
  return null;
};

const getDocumentBoundaryDirection = (key: string): 'backward' | 'forward' | null => {
  if (key === 'ArrowUp' || key === 'Home') return 'backward';
  if (key === 'ArrowDown' || key === 'End') return 'forward';
  return null;
};

const getKeyboardEventKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
  const key: unknown = e.key;
  return typeof key === 'string' ? key : '';
};

const getBoundaryMovement = (e: ReactKeyboardEvent<HTMLDivElement>) => {
  if (e.altKey) return null;

  const key = getKeyboardEventKey(e);

  if (e.metaKey && (key === 'ArrowLeft' || key === 'ArrowRight')) {
    const direction = getLineBoundaryDirection(key);
    return direction ? { direction, granularity: 'lineboundary' as const } : null;
  }

  if (!e.metaKey && !e.ctrlKey && (key === 'Home' || key === 'End')) {
    const direction = getLineBoundaryDirection(key);
    return direction ? { direction, granularity: 'lineboundary' as const } : null;
  }

  if (e.metaKey && (key === 'ArrowUp' || key === 'ArrowDown')) {
    const direction = getDocumentBoundaryDirection(key);
    return direction ? { direction, granularity: 'documentboundary' as const } : null;
  }

  if (e.ctrlKey && (key === 'Home' || key === 'End')) {
    const direction = getDocumentBoundaryDirection(key);
    return direction ? { direction, granularity: 'documentboundary' as const } : null;
  }

  return null;
};

export const handleModifierArrowAroundChipBoundary = (e: ReactKeyboardEvent<HTMLDivElement>, editor: HTMLElement) => {
  const movement = getBoundaryMovement(e);
  if (!movement) return false;

  const sel = getSelectionWithModify();
  if (!sel) return false;

  e.preventDefault();
  const alter: 'move' | 'extend' = e.shiftKey ? 'extend' : 'move';
  const previousFocusNode = sel.focusNode;
  const previousFocusOffset = getSelectionFocusOffset(sel);

  sel.modify(alter, movement.direction, movement.granularity);

  // Boundary movement can stop at contenteditable=false chip edges; nudge past the chip and retry.
  requestAnimationFrame(() => {
    const currentSelection = getSelectionWithModify();
    if (!currentSelection || !editor.contains(currentSelection.focusNode)) return;

    const focusNode = currentSelection.focusNode;
    const focusOffset = getSelectionFocusOffset(currentSelection);
    const didNotMove = focusNode === previousFocusNode && focusOffset === previousFocusOffset;
    const chipBeforeFocus = getChipBeforeFocus(focusNode, focusOffset, editor);
    const chipAfterFocus = getChipAfterFocus(focusNode, focusOffset, editor);

    if (movement.direction === 'right' || movement.direction === 'forward') {
      if (!didNotMove && !chipAfterFocus) return;
      currentSelection.modify(alter, 'forward', 'character');
      currentSelection.modify(alter, movement.direction, movement.granularity);
      return;
    }

    if (!didNotMove && !chipBeforeFocus) return;
    currentSelection.modify(alter, 'backward', 'character');
    currentSelection.modify(alter, movement.direction, movement.granularity);
  });

  return true;
};

export const includeChipAtSelectionFocus = (editor: HTMLElement, direction: 'backward' | 'forward') => {
  requestAnimationFrame(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.anchorNode || !sel.focusNode || !editor.contains(sel.focusNode)) return;

    const chip =
      direction === 'backward'
        ? getChipBeforeFocus(sel.focusNode, getSelectionFocusOffset(sel), editor)
        : getChipAfterFocus(sel.focusNode, getSelectionFocusOffset(sel), editor);
    if (!chip?.parentNode) return;

    const chipIndex: number = Array.from(chip.parentNode.childNodes).indexOf(chip);
    const focusOffset: number = direction === 'backward' ? chipIndex : chipIndex + 1;
    sel.setBaseAndExtent(sel.anchorNode, sel.anchorOffset, chip.parentNode, focusOffset);
  });
};

export const removeChipFromMouseEvent = (e: ReactMouseEvent<HTMLDivElement>, editor: HTMLElement) => {
  const target = e.target as HTMLElement;
  const removeBtn = target.closest?.('.chip-remove');
  if (!removeBtn) return false;

  e.preventDefault();

  const chip = removeBtn.closest(`.${MENTION_CHIP_CLASS as string}`);
  if (!isMentionChip(chip)) return false;

  removeChipWithSpacer(chip);
  dispatchInputEvent(editor);
  return true;
};
